import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Badge, Tag, Statistic, Progress } from 'antd';
import {
  EnvironmentOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  SafetyOutlined,
  GlobalOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { config } from '../config/env';
import { NoMeteredDataCard, savingsReasonText } from './InsufficientDataCard';

const { Title, Text } = Typography;

interface ESGMetrics {
  co2Reduced: number; // tCO2e
  energyReduced: number; // kWh
  waterSaved?: number; // gallons
  complianceScore: number; // 0-100
  certifications: string[];
}

export const ESGCredentials: React.FC = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<ESGMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  useEffect(() => {
    fetchESGData();
  }, [user?.id]);

  const fetchESGData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(
        `${config.apiUrl}/api/energy/savings/metrics?days=365`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const payload = response.data.savings;
      if (payload?.status === 'ok' && payload.metrics) {
        const m = payload.metrics;
        const co2Reduced = m.co2Reduced ?? 0; // tCO2e
        const energyReduced = m.kWhReduced ?? 0;

        // Compliance score is a documented heuristic on top of real figures.
        const complianceScore = Math.min(100, 60 + co2Reduced * 10);

        const certifications: string[] = [];
        if (co2Reduced > 1) certifications.push('Carbon Neutral Operations');
        if (m.paybackMonths != null && m.paybackMonths < 12)
          certifications.push('Energy Efficiency Certified');
        if (complianceScore > 80) certifications.push('LEED Compliant');
        if (energyReduced > 1000) certifications.push('Green Energy Champion');

        setMetrics({
          co2Reduced,
          energyReduced,
          complianceScore,
          certifications,
        });
        setUnavailable(null);
      } else {
        // P1 M&V: no verified metrics → no ESG claims at all.
        setMetrics(null);
        setUnavailable(savingsReasonText(payload));
      }
    } catch (error) {
      console.error('Failed to fetch ESG data:', error);
      setMetrics(null);
      setUnavailable(
        'Savings service unreachable — ESG figures are not shown rather than estimated.',
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Card loading={loading} />;
  }

  if (!metrics) {
    // P1 M&V: explain the absence instead of silently hiding or faking numbers.
    return (
      <NoMeteredDataCard
        title="ESG figures need metered data"
        detail={unavailable ?? undefined}
      />
    );
  }

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <GlobalOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
        <Title level={3}>ESG Credentials & Compliance</Title>
        <Text type="secondary">
          Track your environmental impact and meet regulatory requirements
        </Text>
      </div>

      {/* Environmental Impact */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center', height: '100%' }}>
            <EnvironmentOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '12px' }} />
            <Statistic
              title="CO₂ Reduced"
              value={metrics.co2Reduced}
              suffix="tCO₂e"
              valueStyle={{ color: '#52c41a', fontSize: '24px' }}
              precision={2}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Annual reduction
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center', height: '100%' }}>
            <TrophyOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }} />
            <Statistic
              title="Energy Reduced"
              value={metrics.energyReduced}
              suffix="kWh"
              valueStyle={{ color: '#1890ff', fontSize: '24px' }}
              formatter={(value) => Number(value).toLocaleString()}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Annual reduction
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center', height: '100%' }}>
            <SafetyOutlined style={{ fontSize: '32px', color: '#722ed1', marginBottom: '12px' }} />
            <Statistic
              title="Compliance Score"
              value={metrics.complianceScore}
              suffix="/100"
              valueStyle={{ 
                color: metrics.complianceScore >= 80 ? '#52c41a' : metrics.complianceScore >= 60 ? '#1890ff' : '#faad14',
                fontSize: '24px'
              }}
            />
            <Progress
              percent={metrics.complianceScore}
              strokeColor={
                metrics.complianceScore >= 80 ? '#52c41a' : metrics.complianceScore >= 60 ? '#1890ff' : '#faad14'
              }
              showInfo={false}
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Certifications & Badges */}
      <Card 
        size="small" 
        title={
          <span>
            <FileTextOutlined style={{ marginRight: '8px' }} />
            Your Certifications & Badges
          </span>
        }
        style={{ marginBottom: '24px' }}
      >
        {metrics.certifications.length > 0 ? (
          <Row gutter={[16, 16]}>
            {metrics.certifications.map((cert, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <Badge.Ribbon text="Active" color="green">
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }} />
                    <Text strong style={{ display: 'block' }}>{cert}</Text>
                  </Card>
                </Badge.Ribbon>
              </Col>
            ))}
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Text type="secondary">
              Start optimizing to earn certifications. Reduce energy consumption to unlock badges.
            </Text>
          </div>
        )}
      </Card>

      {/* Compliance Benefits */}
      <Card size="small" title="Compliance & Regulatory Benefits">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
              <Text strong>LEED Certification Support</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Track energy performance metrics required for LEED certification
              </Text>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
              <Text strong>Energy Star Compliance</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Meet Energy Star building requirements with verified data
              </Text>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
              <Text strong>Carbon Tax Preparedness</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Reduce future carbon tax liability through verified emissions reductions
              </Text>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
              <Text strong>ESG Reporting Ready</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Export compliance-ready reports for ESG disclosures
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Growth & Future-Proofing */}
      <Card 
        size="small" 
        style={{ 
          marginTop: '24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={16}>
            <Title level={4} style={{ color: 'white', marginBottom: '8px' }}>
              Future-Proof Your Operations
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              As carbon regulations tighten and ESG reporting becomes mandatory, 
              your data-driven approach positions you ahead of competitors. 
              Scale operations while reducing environmental impact.
            </Text>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <Tag color="gold" style={{ fontSize: '16px', padding: '8px 16px' }}>
              Investment Timeline
            </Tag>
            <div style={{ marginTop: '16px' }}>
              <Text style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                ROI in 6-8 months
              </Text>
              <br />
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                Then pure profit + compliance
              </Text>
            </div>
          </Col>
        </Row>
      </Card>
    </Card>
  );
};

export default ESGCredentials;

